import {Mail} from "./Mail";
import {Contact} from "./Contact";
import {User} from "./User";
import {EventUserSpeaker} from "./EventUserSpeaker";
import {EventContactSpeaker} from "./EventContactSpeaker";
import {MeetingRoom} from "./MeetingRoom";
import {GroupEvent} from "./GroupEvent";
import {EventUserAdditionalGuest} from "./EventUserAdditionalGuest";
import {EventContactAdditionalGuest} from "./EventContactAdditionalGuest";
import {UploadedFile} from "./File";
import {UploadableFile} from "../common/file/UploadableFile";
import {convertBooleanToQueryStringValue} from "../common/utils/booleanUtils";

export enum EventStatus {
    DRAFT = "DRAFT",
    MEETING_ROOM_RESERVED = "MEETING_ROOM_RESERVED",
    INVITATIONS_SENT = "INVITATIONS_SENT",
    FINISHED = "FINISHED",
    CANCELLED = "CANCELLED"
}

export enum EventView {
    CURRENT = "current",
    PAST = "past",
    CANCELLED = "cancelled"
}

type EventViewDefinition = {
    label: string;
    filters: {
        toGo?: string;
        passed?: string;
        statusIn: string;
    };
};

// Single definition of the event views: the home page and the event list both use it,
// so they always show the same events for a given view.
export const EVENT_VIEWS: Record<EventView, EventViewDefinition> = {
    [EventView.CURRENT]: {
        label: "Événements à venir",
        filters: {
            toGo: convertBooleanToQueryStringValue(true),
            statusIn: [EventStatus.DRAFT, EventStatus.MEETING_ROOM_RESERVED, EventStatus.INVITATIONS_SENT].join(","),
        },
    },
    [EventView.PAST]: {
        label: "Événements passés",
        filters: {
            passed: convertBooleanToQueryStringValue(true),
            statusIn: [EventStatus.MEETING_ROOM_RESERVED, EventStatus.INVITATIONS_SENT, EventStatus.FINISHED].join(","),
        },
    },
    [EventView.CANCELLED]: {
        label: "Événements annulés",
        filters: {
            statusIn: EventStatus.CANCELLED,
        },
    },
};

export enum AttendanceType {
    ON_SITE = "ON_SITE",
    DISTANCE = "DISTANCE",
    HYBRID = "HYBRID"
}

export interface Event {
    id: string;
    createdAt: string;
    updatedAt: string;
    description: string;
    status: EventStatus;
    googleMeetLink: string | null;
    googleMeetConferenceId: string | null;
    startAt: string;
    endAt: string;
    googleMeetAttendanceSyncedAt: string | null;
    attendanceType: AttendanceType;
    mail?: Mail;
    author?: User;
    contactAnimator?: Contact;
    userAnimator?: User;
    eventUserSpeakers?: Array<EventUserSpeaker>;
    eventContactSpeakers?: Array<EventContactSpeaker>;
    meetingRoom?: MeetingRoom;
    groupEvents?: Array<GroupEvent>;
    eventUserAdditionalGuests?: Array<EventUserAdditionalGuest>;
    eventContactAdditionalGuests?: Array<EventContactAdditionalGuest>;
    displayStatus?: string;
    displayAttendanceType?: string;
    isLinkedWithCommitteeOrWorkshop?: boolean;
    googleCalendarEventId?: string;
    eventStatistics:{[key: string]: number};
    registeredContactsNb?: number;
    allowDelegation: boolean;
    maxDelegationsPerGuest: number | null;
    allowCompanion: boolean;
    maxCompanionsPerGuest: number | null;
    allowPowerDelegation: boolean;
    invitedContactIds?: Array<string>;
}

export type ReadonlyEvent = Readonly<Event>;

export interface EventDto {
    subject: string;
    content: string;
    attachments: Array<UploadedFile | UploadableFile>;
    description: string;
    authorId: string;
    contactAnimatorId: string;
    userAnimatorId: string;
    startAt: string;
    endAt: string;
    meetingRoomId: string;
    principalGroupId: string | null;
    groupIds: Array<string>;
    userSpeakerIds: Array<string>;
    contactSpeakerIds: Array<string>;
    userAdditionalGuestIds: Array<string>;
    contactAdditionalGuestIds: Array<string>;
    attendanceType: string;
    allowDelegation: boolean;
    maxDelegationsPerGuest: number | null;
    allowCompanion: boolean;
    maxCompanionsPerGuest: number | null;
    allowPowerDelegation: boolean;
}

export interface ManageGoogleCalendarEventDto {
    meetingRoomId?: string;
    startAt: string;
    endAt: string;
    attendanceType: AttendanceType;
}

export function isEventFinished(event: ReadonlyEvent): boolean {
    return event.status === EventStatus.FINISHED;
}

export function isHybridEvent(event: ReadonlyEvent): boolean {
    return event.attendanceType === AttendanceType.HYBRID;
}

export function isOnSiteEvent(event: ReadonlyEvent): boolean {
    return event.attendanceType === AttendanceType.ON_SITE;
}

export function isDistanceEvent(event: ReadonlyEvent): boolean {
    return event.attendanceType === AttendanceType.DISTANCE;
}

export function isMeetingRoomReserved(event: ReadonlyEvent): boolean {
    return event.status === EventStatus.MEETING_ROOM_RESERVED;
}

export function isDraftEvent(event: ReadonlyEvent): boolean {
    return event.status === EventStatus.DRAFT;
}

export function hasNoGoogleCalendarEvent(event: ReadonlyEvent): boolean {
    return !event.googleCalendarEventId;
}

export function reachedInvitationsSent(event: ReadonlyEvent): boolean {
    return [EventStatus.INVITATIONS_SENT, EventStatus.FINISHED, EventStatus.CANCELLED].includes(event.status);
}

export function canEditEventDates(event: ReadonlyEvent): boolean {
    return event.status === EventStatus.MEETING_ROOM_RESERVED
        || event.status === EventStatus.INVITATIONS_SENT;
}

export function canCreateGoogleMeet(event: ReadonlyEvent): boolean {
    return event.status === EventStatus.INVITATIONS_SENT
        && event.attendanceType !== AttendanceType.ON_SITE
        && Boolean(event.googleCalendarEventId)
        && event.googleMeetLink === null;
}

export function canRemoveGoogleMeet(event: ReadonlyEvent): boolean {
    return event.status === EventStatus.INVITATIONS_SENT
        && Boolean(event.googleCalendarEventId)
        && (event.googleMeetLink !== null || event.googleMeetConferenceId !== null);
}

export function isEventPassed(event: ReadonlyEvent): boolean {
    return new Date(event.endAt) < new Date();
}

// Mirrors Event::canBeMarkedAsFinished() on the backend.
export function canMarkEventAsFinished(event: ReadonlyEvent): boolean {
    return [EventStatus.DRAFT, EventStatus.MEETING_ROOM_RESERVED, EventStatus.INVITATIONS_SENT].includes(event.status)
        && isEventPassed(event);
}

export function isEventUpcoming(event: ReadonlyEvent): boolean {
    return new Date(event.startAt) > new Date();
}
